import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Text,
  Button,
  Input,
  Tooltip,
  Combobox,
  Option,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  DialogTrigger,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  TableCellLayout,
  Skeleton,
  SkeletonItem,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  createTableColumn,
  mergeClasses,
  type TableColumnDefinition,
  type TableColumnSizingOptions,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  AddRegular,
  EditRegular,
  CheckmarkRegular,
  DismissRegular,
  DeleteRegular,
  ArrowUndoRegular,
  InfoFilled,
} from '@fluentui/react-icons';
import { useProgramChangesPageStyles } from '../../styles/ProgramChangesPage.styles';
import { exportToCsv } from '../../utils/exportToCsv';
import { COUNTRIES } from '../../constants/countries';
import { compareString, compareNumber } from '../../utils/comparators';
import {
  useMarketCategories,
} from '../../contexts/MarketCategoriesContext';
import {
  ZERO_THRESHOLDS,
  type MarketCategory,
  type PendingMarketCategoryInfo,
} from '../../services/marketCategoriesService';
import {
  getFriendlyApiErrorMessage,
  logApiError,
} from '../../services/apiErrorMessages';
import { formatPendingTooltip as formatSharedPendingTooltip, type FormatPendingTooltipOptions } from './formatPendingTooltip';
import { ProgramChangesLastUpdatedHeader } from './ProgramChangesLastUpdatedHeader';

interface MarketCategoriesTabProps {
  isLocked: boolean;
}

type ColumnId = 'name' | 'countries' | 'actions';

// Sentinel id for the unsaved "new category" row.
const PENDING_ID = '__pending_new_category__';

// Auto-generates the next category name as max(existing integer names) + 1.
const computeNextCategoryName = (categories: readonly MarketCategory[]): string => {
  let max = 0;
  for (const c of categories) {
    const trimmed = c.name.trim();
    if (/^\d+$/.test(trimmed)) { 
      const n = parseInt(trimmed, 10);
      if (n > max) max = n;
    }
  }
  return String(max + 1);
};

const formatPendingTooltip = (
  pending: PendingMarketCategoryInfo,
  options?: FormatPendingTooltipOptions,
): string =>
  formatSharedPendingTooltip(pending, options);

// Master catalog of selectable countries.
const allCountries: readonly string[] = COUNTRIES;

const columnWeights: Record<ColumnId, number> = {
  name: 0.5,
  countries: 30,
  actions: 0.5,
};

const columnMinWidths: Record<ColumnId, number> = {
  name: 160,
  countries: 380,
  actions: 220,
};

interface CategoriesSkeletonProps {
  styles: ReturnType<typeof useProgramChangesPageStyles>;
  rows?: number;
  showActions?: boolean;
}

const CategoriesSkeleton: React.FC<CategoriesSkeletonProps> = ({
  styles,
  rows = 4,
  showActions = true,
}) => {
  const pillsPerRow = [6, 4, 8, 5, 7];
  const pillWidths = [80, 110, 70, 95, 60, 130, 85, 100];
  return (
    <Skeleton aria-label="Loading market categories" className={styles.skeletonBlock}>
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div key={rowIdx} className={styles.skeletonRow}>
          <div className={styles.skeletonNameCell}>
            <SkeletonItem shape="rectangle" />
          </div>
          <div className={styles.skeletonCountriesCell}>
            {Array.from({ length: pillsPerRow[rowIdx % pillsPerRow.length] }).map((__, i) => (
              <SkeletonItem
                key={i}
                shape="rectangle"
                className={styles.skeletonPill}
                style={{ width: `${pillWidths[(rowIdx + i) % pillWidths.length]}px` }}
              />
            ))}
          </div>
          {showActions && (
            <div className={styles.skeletonActionsCell}>
              <SkeletonItem shape="rectangle" className={styles.skeletonActionSquare} />
              <SkeletonItem shape="rectangle" className={styles.skeletonActionSquare} />
            </div>
          )}
        </div>
      ))}
    </Skeleton>
  );
};

export const MarketCategoriesTab: React.FC<MarketCategoriesTabProps> = ({ isLocked }) => {
  const styles = useProgramChangesPageStyles();
  const {
    categories,
    loading,
    loadError,
    refresh,
    addCategory,
    updateName,
    updateCountries,
    removeCategory,
    cancelPendingChange,
  } = useMarketCategories();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState<string>('');
  const [draftCountries, setDraftCountries] = useState<string[] | null>(null);
  const [pendingRow, setPendingRow] = useState<MarketCategory | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Add-country popup state
  const [addPopupOpen, setAddPopupOpen] = useState<boolean>(false);
  const [comboboxValue, setComboboxValue] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  // Delete confirmation dialog
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Container ref + measured width for proportional column sizing.
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const columnSizingOptions = useMemo<TableColumnSizingOptions>(() => {
    const activeIds: ColumnId[] = ['name', 'countries'];
    if (!isLocked) activeIds.push('actions');

    const totalMin = activeIds.reduce((sum, id) => sum + columnMinWidths[id], 0);
    const totalWeight = activeIds.reduce((sum, id) => sum + columnWeights[id], 0);
    const available = Math.max(containerWidth - 2, totalMin);
    const extra = Math.max(0, available - totalMin);

    const sizing: TableColumnSizingOptions = {};
    activeIds.forEach((id) => {
      const ideal = columnMinWidths[id] + (extra * columnWeights[id]) / totalWeight;
      sizing[id] = {
        minWidth: columnMinWidths[id],
        idealWidth: Math.round(ideal),
      };
    });
    return sizing;
  }, [containerWidth, isLocked]);

  // The list rendered in the grid: pending row (if any) goes first.
  const displayedCategories = useMemo<MarketCategory[]>(
    () => (pendingRow ? [pendingRow, ...categories] : categories),
    [pendingRow, categories],
  );

  const hasPendingChanges = useMemo(
    () => categories.some((c) => !!c.pending),
    [categories],
  );
  const hasPendingRename = useMemo(
    () => categories.some((c) => c.pending?.operation === 'rename'),
    [categories],
  );
  const hasPendingDelete = useMemo(
    () => categories.some((c) => c.pending?.operation === 'delete'),
    [categories],
  );
  const hasPendingCreate = useMemo(
    () => categories.some((c) => c.pending?.operation === 'create'),
    [categories],
  );
  const hasCountryAdded = useMemo(
    () => categories.some((c) => (c.pendingCountryDiff?.added?.length ?? 0) > 0),
    [categories],
  );
  const hasCountryRemoved = useMemo(
    () => categories.some((c) => (c.pendingCountryDiff?.removed?.length ?? 0) > 0),
    [categories],
  );
  // Countries that appear as "added" in any category's pending diff are
  // considered reassigned. A country removed from category A but added to
  // category B in the same pending batch will NOT be discarded by the sync,
  // so it should not appear in the "removed (will be ignored)" warning UI.
  const reassignedCountries = useMemo(() => {
    const set = new Set<string>();
    for (const c of categories) {
      for (const country of c.pendingCountryDiff?.added ?? []) {
        set.add(country.toLowerCase());
      }
    }
    return set;
  }, [categories]);
  const getUnreassignedRemoved = (category: MarketCategory): string[] =>
    (category.pendingCountryDiff?.removed ?? []).filter(
      (country) => !reassignedCountries.has(country.toLowerCase()),
    );

  const isPending = (id: string) => id === PENDING_ID;

  const startEdit = (category: MarketCategory) => {
    setEditingId(category.id);
    setDraftName(category.name);
    setDraftCountries([...category.countries]);
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftName('');
    setDraftCountries(null);
    setPendingRow(null);
    setSaveError(null);
  };

  const handleNewCategory = () => {
    if (isLocked || pendingRow) return;
    const nextName = computeNextCategoryName(categories);
    const newRow: MarketCategory = {
      id: PENDING_ID,
      name: nextName,
      countries: [],
      thresholds: { ...ZERO_THRESHOLDS },
    };
    setPendingRow(newRow);
    setEditingId(PENDING_ID);
    setDraftName(nextName);
    setDraftCountries([]);
    setSaveError(null);
  };

  const trimmedDraftName = draftName.trim();

  const nameError = useMemo<string | null>(() => {
    if (!editingId) return null;
    if (!trimmedDraftName) return null;
    const lc = trimmedDraftName.toLowerCase();
    const dup = categories.some(
      (c) => c.id !== editingId && c.name.trim().toLowerCase() === lc,
    );
    return dup ? 'A category with this name already exists.' : null;
  }, [editingId, trimmedDraftName, categories]);
  
  const countriesError = useMemo<string | null>(() => {
    if (editingId !== PENDING_ID) return null;
    if (!draftCountries || draftCountries.length > 0) return null;
    return 'Add at least one country to create a market category.';
  }, [editingId, draftCountries]);

  const saveEdit = async () => {
    if (!editingId || draftCountries === null) return;
    if (!trimmedDraftName || nameError) return;
    if (countriesError) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (editingId === PENDING_ID) {
        const created = await addCategory({ name: trimmedDraftName });
        await updateCountries(created.id, draftCountries);
      } else {
        const original = categories.find((c) => c.id === editingId);
        if (!original) {
          setSaving(false);
          return;
        }
        if (original.name !== trimmedDraftName) {
          await updateName(editingId, trimmedDraftName);
        }
        const sameCountries =
          original.countries.length === draftCountries.length &&
          original.countries.every((c, i) => c === draftCountries[i]);
        if (!sameCountries) {
          await updateCountries(editingId, draftCountries);
        }
      }
      cancelEdit();
      await refresh();
    } catch (err) {
      logApiError('MarketCategoriesTab.saveEdit', err);
      setSaveError(getFriendlyApiErrorMessage(err, 'Failed to save category.'));
    } finally {
      setSaving(false);
    }
  };

  const removeDraftCountry = (country: string) => {
    setDraftCountries((prev) => (prev ? prev.filter((c) => c !== country) : prev));
  };

  const usedElsewhere = useMemo(() => {
    const set = new Set<string>();
    categories.forEach((cat) => {
      if (cat.id === editingId) return;
      cat.countries.forEach((c) => set.add(c.toLowerCase()));
    });
    return set;
  }, [categories, editingId]);

  const availableCountries = useMemo(() => {
    const draftSet = new Set((draftCountries ?? []).map((c) => c.toLowerCase()));
    return allCountries.filter(
      (c) => !usedElsewhere.has(c.toLowerCase()) && !draftSet.has(c.toLowerCase()),
    );
  }, [usedElsewhere, draftCountries]);

  const filteredOptions = useMemo(() => {
    const q = comboboxValue.trim().toLowerCase();
    if (!q) return availableCountries;
    return availableCountries.filter((c) => c.toLowerCase().includes(q));
  }, [availableCountries, comboboxValue]);

  const openAddPopup = () => {
    setComboboxValue('');
    setSelectedCountry(null);
    setAddPopupOpen(true);
  };

  const closeAddPopup = () => {
    setAddPopupOpen(false);
    setComboboxValue('');
    setSelectedCountry(null);
  };

  const confirmAddCountry = () => {
    if (!selectedCountry || !draftCountries) return;
    const lc = selectedCountry.toLowerCase();
    if (
      usedElsewhere.has(lc) ||
      draftCountries.some((c) => c.toLowerCase() === lc)
    ) {
      closeAddPopup();
      return;
    }
    setDraftCountries([...draftCountries, selectedCountry]);
    closeAddPopup();
  };

  const requestDelete = (id: string) => setPendingDeleteId(id);

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    try {
      await removeCategory(id);
      if (editingId === id) cancelEdit();
      await refresh();
    } catch (err) {
      logApiError('MarketCategoriesTab.confirmDelete', err);
      setSaveError(getFriendlyApiErrorMessage(err, 'Failed to remove category.'));
    }
  };

  const handleUndoPending = async (id: string) => {
    try {
      await cancelPendingChange(id);
      await refresh();
    } catch (err) {
      logApiError('MarketCategoriesTab.handleUndoPending', err);
      setSaveError(getFriendlyApiErrorMessage(err, 'Failed to cancel pending change.'));
    }
  };

  const columns: TableColumnDefinition<MarketCategory>[] = useMemo(() => {
    const cols: TableColumnDefinition<MarketCategory>[] = [
      createTableColumn<MarketCategory>({
        columnId: 'name',
        compare: (a, b) => compareString(a.name, b.name),
        renderHeaderCell: () => 'Market category',
        renderCell: () => null,
      }),
      createTableColumn<MarketCategory>({
        columnId: 'countries',
        compare: (a, b) => compareNumber(a.countries.length, b.countries.length),
        renderHeaderCell: () => 'Countries',
        renderCell: () => null,
      }),
    ];

    if (!isLocked) {
      cols.push(
        createTableColumn<MarketCategory>({
          columnId: 'actions',
          renderHeaderCell: () => '',
          renderCell: () => null,
        }),
      );
    }

    return cols;
  }, [isLocked]);

  const renderCellContent = (columnId: ColumnId, category: MarketCategory) => {
    const isEditing = editingId === category.id;

    if (columnId === 'name') {
      if (isEditing) {
        return (
          <div className={styles.benefitNameContent}>
            <Input
              value={draftName}
              disabled
              placeholder="Category name"
              onChange={(_, data) => {
                setDraftName(data.value);
                if (saveError) setSaveError(null);
              }}
              aria-label="Category name"
              className={styles.benefitNameInput}
              autoFocus={isPending(category.id)}
            />
          </div>
        );
      }
      const pending = category.pending;
      const nameClassName = pending?.operation === 'create'
        ? styles.categoryNamePendingCreate
        : pending?.operation === 'rename'
          ? styles.categoryNamePendingRename
          : pending?.operation === 'delete'
            ? styles.categoryNamePendingDelete
            : undefined;
      const nameNode = (
        <Text weight="semibold" className={nameClassName}>
          {category.name}
        </Text>
      );
      return (
        <div className={styles.categoryNameCell}>
          {pending && (
            <Tooltip
              relationship="description"
              content={formatPendingTooltip(pending, {
                removedCountries: getUnreassignedRemoved(category),
              })}
            >
              <span
                className={styles.pendingInfoIcon}
                role="img"
                aria-label="Pending change details"
                tabIndex={0}
              >
                <InfoFilled />
              </span>
            </Tooltip>
          )}
          {nameNode}
        </div>
      );
    }

    if (columnId === 'actions') {
      if (isEditing) {
        return (
          <div className={styles.editActions}>
            <Button
              appearance="primary"
              icon={<CheckmarkRegular />}
              onClick={saveEdit}
              size="small"
              disabled={saving || !trimmedDraftName || !!nameError || !!countriesError}
            >
              Save
            </Button>
            <Tooltip content="Cancel" relationship="label">
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={cancelEdit}
                aria-label="Cancel"
                size="small"
              />
            </Tooltip>
            <span className={styles.editActionsSpacer} aria-hidden />
            {!isPending(category.id) && (
              <Tooltip content="Delete category" relationship="label">
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={() => requestDelete(category.id)}
                  aria-label={`Delete ${category.name || 'category'}`}
                  className={styles.deleteButton}
                  size="small"
                />
              </Tooltip>
            )}
          </div>
        );
      }
      const isPendingDelete = category.pending?.operation === 'delete';
      if (isPendingDelete) {
        return (
          <Tooltip content="Undo delete (cancel pending)" relationship="label">
            <Button
              appearance="subtle"
              icon={<ArrowUndoRegular />}
              onClick={() => handleUndoPending(category.id)}
              aria-label={`Undo delete of ${category.name}`}
              size="small"
            />
          </Tooltip>
        );
      }
      return (
        <Tooltip content="Edit" relationship="label">
          <Button
            appearance="subtle"
            icon={<EditRegular />}
            onClick={() => startEdit(category)}
            aria-label={`Edit ${category.name}`}
            size="small"
          />
        </Tooltip>
      );
    }

    // countries
    if (isEditing && draftCountries) {
      return (
        <div>
          <div className={styles.countriesList}>
            {draftCountries.map((country) => (
              <span key={country} className={styles.countryChipEditable}>
                <span className={styles.countryChipLabel}>{country}</span>
                <button
                  type="button"
                  className={styles.countryRemoveX}
                  aria-label={`Remove ${country}`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeDraftCountry(country);
                  }}
                >
                  <DismissRegular />
                </button>
              </span>
            ))}
            <Tooltip content="Add country" relationship="label">
              <Button
                appearance="outline"
                shape="circular"
                size="small"
                icon={<AddRegular />}
                onClick={openAddPopup}
                aria-label="Add country"
              />
            </Tooltip>
            {countriesError && (
              <Text size={200} className={styles.inlineErrorText}>
                {countriesError}
              </Text>
            )}
          </div>
          {(nameError || saveError) && (
            <div className={styles.inlineError}>
              <Text size={200}>{nameError ?? saveError}</Text>
            </div>
          )}
        </div>
      );
    }

    const diff = category.pendingCountryDiff;
    const addedSet = new Set((diff?.added ?? []).map((c) => c.toLowerCase()));
    const removedList = diff?.removed ?? [];
    const isCountryAdded = (country: string) => addedSet.has(country.toLowerCase());
    const hasAnyChip = category.countries.length > 0 || removedList.length > 0;

    return (
      <div className={styles.countriesList}>
        {!hasAnyChip ? (
          <Text size={200} italic>No countries assigned</Text>
        ) : (
          <>
            {category.countries.map((country) => {
              const added = isCountryAdded(country);
              const className = added
                ? mergeClasses(styles.countryBadge, styles.countryBadgeAdded)
                : styles.countryBadge;
              const title = added ? 'Pending: this country will be added' : undefined;
              return (
                <span key={`kept-${country}`} className={className} title={title}>
                  {country}
                </span>
              );
            })}
            {removedList.map((country) => {
              const reassigned = reassignedCountries.has(country.toLowerCase());
              const title = reassigned
                ? 'Pending: this country will be removed and reassigned to another category.'
                : 'Pending: this country will be removed. If not reassigned to another category before the sync runs, the removal will be ignored.';
              return (
                <span
                  key={`removed-${country}`}
                  className={mergeClasses(styles.countryBadge, styles.countryBadgeRemoved)}
                  title={title}
                >
                  {country}
                </span>
              );
            })}
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <ProgramChangesLastUpdatedHeader selectedTab="market-categories" />

      <div className={styles.toolbar}>
        <Button
          appearance="subtle"
          icon={<AddRegular />}
          disabled={isLocked || !!pendingRow}
          onClick={handleNewCategory}
        >
          New category
        </Button>
        <Button
          appearance="subtle"
          icon={<ArrowDownloadRegular />}
          onClick={() =>
            exportToCsv<MarketCategory>({
              fileName: 'market-categories',
              columns: [
                { header: 'Market category', accessor: (c) => c.name },
                {
                  header: 'Countries',
                  accessor: (c) => c.countries.join(', ')
                },
              ],
              rows: categories,
            })
          }
        >
          Export to CSV
        </Button>
      </div>

      <div className={styles.benefitsCount}>
        <Text size={200}>
          {categories.length} categories
          {pendingRow ? ' (1 unsaved)' : ''}
        </Text>
      </div>

      {loadError && (
        <div className={styles.errorBar}>
          <MessageBar intent="error">
            <MessageBarBody>{loadError}</MessageBarBody>
            <MessageBarActions>
              <Button appearance="transparent" onClick={() => void refresh()}>
                Retry
              </Button>
            </MessageBarActions>
          </MessageBar>
        </div>
      )}

      <div ref={gridContainerRef} className={styles.gridContainer}>
        {hasPendingChanges ? (
          <div className={styles.pendingLegend} role="note" aria-label="Pending changes legend">
            <span><strong>Pending changes legend:</strong></span>
            {hasPendingCreate ? (
              <span className={styles.pendingLegendItem}>
                <span className={styles.categoryNameSwatchCreate} aria-hidden>Aa</span>
                New category
              </span>
            ) : null}
            {hasPendingRename ? (
              <span className={styles.pendingLegendItem}>
                <span className={styles.categoryNameSwatchRename} aria-hidden>Aa</span>
                Renamed category
              </span>
            ) : null}
            {hasPendingDelete ? (
              <span className={styles.pendingLegendItem}>
                <span className={styles.categoryNameSwatchDelete} aria-hidden>Aa</span>
                Category to be removed
              </span>
            ) : null}
            {hasCountryAdded ? (
              <span className={styles.pendingLegendItem}>
                <span
                  className={mergeClasses(styles.pendingLegendSwatch, styles.countryBadgeAdded)}
                  aria-hidden
                />
                Country to be added
              </span>
            ) : null}
            {hasCountryRemoved ? (
              <span className={styles.pendingLegendItem}>
                <span
                  className={mergeClasses(styles.pendingLegendSwatch, styles.countryBadgeRemoved)}
                  aria-hidden
                />
                Country to be removed
              </span>
            ) : null}
          </div>
        ) : null}
        {loading && categories.length === 0 ? (
          <CategoriesSkeleton styles={styles} showActions={!isLocked} />
        ) : (
          <DataGrid
          key={`grid-${Math.round(containerWidth / 40)}-${isLocked ? 'l' : 'u'}`}
          items={displayedCategories}
          columns={columns}
          sortable
          resizableColumns
          resizableColumnsOptions={{ autoFitColumns: false }}
          columnSizingOptions={columnSizingOptions}
          getRowId={(item) => (item as MarketCategory).id}
          size="medium"
        >
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell focusMode="none">{renderHeaderCell()}</DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<MarketCategory>>
            {({ item, rowId }) => (
              <DataGridRow<MarketCategory>
                key={rowId}
                className={editingId === item.id ? styles.benefitRowEditing : undefined}
              >
                {({ columnId }) => (
                  <DataGridCell>
                    <TableCellLayout truncate={false}>
                      {renderCellContent(columnId as ColumnId, item)}
                    </TableCellLayout>
                  </DataGridCell>
                )}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
        )}
      </div>

      {/* Add country dialog */}
      <Dialog
        open={addPopupOpen}
        onOpenChange={(_, data) => {
          if (!data.open) closeAddPopup();
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Add country</DialogTitle>
            <DialogContent>
              <div className={styles.addCountryDialogContent}>
                <Text size={200}>
                  Select a country. Countries already assigned to another category are not listed.
                </Text>
                <Combobox
                  placeholder="Search country..."
                  value={comboboxValue}
                  selectedOptions={selectedCountry ? [selectedCountry] : []}
                  onInput={(e) =>
                    setComboboxValue((e.target as HTMLInputElement).value)
                  }
                  onOptionSelect={(_, data) => {
                    const value = data.optionValue ?? null;
                    setSelectedCountry(value);
                    setComboboxValue(value ?? '');
                  }}
                  freeform={false}
                >
                  {filteredOptions.length === 0 ? (
                    <Option key="__no_results__" value="" disabled text="">
                      No countries available
                    </Option>
                  ) : (
                    filteredOptions.map((country) => (
                      <Option key={country} value={country}>
                        {country}
                      </Option>
                    ))
                  )}
                </Combobox>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={confirmAddCountry}
                disabled={!selectedCountry}
              >
                Add
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={pendingDeleteId !== null}
        onOpenChange={(_, data) => {
          if (!data.open) setPendingDeleteId(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete category?</DialogTitle>
            <DialogContent>
              {(() => {
                const target = categories.find((c) => c.id === pendingDeleteId);
                const isPendingCreate = target?.pending?.operation === 'create';
                if (isPendingCreate) {
                  return (
                    <>
                      <Text>
                        Discard the pending creation of{' '}
                        <strong>{target?.name ?? 'this category'}</strong>? It
                        hasn't been applied yet, so this just removes the
                        pending change.
                      </Text>
                      <MessageBar intent="warning" className={styles.deleteDialogWarning}>
                        <MessageBarBody>
                          This category is <strong>pending creation</strong> and has
                          not been applied yet. Discarding it removes the pending
                          change immediately and <strong>cannot be undone</strong>.
                        </MessageBarBody>
                      </MessageBar>
                    </>
                  );
                }
                const pendingOp = target?.pending?.operation;
                const hasPendingEdit =
                  pendingOp === 'update' ||
                  pendingOp === 'rename' ||
                  !!target?.pendingThresholds ||
                  (target?.pendingCountryDiff?.added?.length ?? 0) > 0 ||
                  (target?.pendingCountryDiff?.removed?.length ?? 0) > 0;
                return (
                  <>
                    <Text>
                      Are you sure you want to delete{' '}
                      <strong>{target?.name ?? 'this category'}</strong>? This
                      will also remove its thresholds.
                    </Text>
                    {hasPendingEdit && (
                      <MessageBar intent="warning" className={styles.deleteDialogWarning}>
                        <MessageBarBody>
                          This category has pending changes that have not been
                          applied yet. Deleting it will discard those pending
                          changes.
                        </MessageBarBody>
                      </MessageBar>
                    )}
                  </>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={confirmDelete}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
